import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  SelectChangeEvent,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { GridDeleteIcon } from "@mui/x-data-grid";
import { useEffect, useMemo, useRef, useState } from "react";
import useStyles from "./styles";
import { Add } from "@mui/icons-material";

type Divider = "" | "divider" | "dividerDashed";

interface GeralMenuProps {
  card: DashCardsGraphics | undefined;
  updateCard: (newCard: DashCardsGraphics) => void;
}

const GeralMenu: React.FC<GeralMenuProps> = ({ card, updateCard }) => {
  const theme = useTheme();
  const styles = useStyles(theme, card?.split);

  const [titleValue, setTitleValue] = useState<string | undefined>(card?.title);
  const [subtitleValue, setSubtitleValue] = useState<string | undefined>(
    card?.subtitle
  );
  const [descriptionValue, setDescriptionValue] = useState<string | undefined>(
    card?.description
  );
  const [backgroundChecked, setBackgroundChecked] = useState<boolean>(
    card?.background ?? false
  );
  const [removePaddingChecked, setRemovePaddingChecked] = useState<boolean>(
    card?.noPadding ?? false
  );
  const [separadorValue, setSeparadorValue] = useState<
    "divider" | "dividerDashed" | ""
  >(card?.separator ?? "");
  const [orientationValue, setOrientationValue] = useState<boolean>(
    hasInitialOrientation(card?.orientation ?? "")
  );
  const [breakpointsSelected, setBreakpointsSelected] = useState<
    { id: string; breakpoint: string; orientation: string }[]
  >(getInitialOrientation(card?.orientation ?? ""));
  // const [orientationObject, setOrientationObject] = useState<
  //   Record<string, string>
  // >({});
  const [dividirValue, setDividirValue] = useState<boolean>(
    card?.split ?? false
  );
  const [equalDivision, setEqualDivision] = useState<boolean>(
    card?.divisionEqual ?? true
  );

  const isTypeCard = useMemo(() => {
    return card?.graphics?.map((graph) => graph.type).includes("cards");
  }, [card]);

  const isInitialMount = useRef(true);

  const updateParent = () => {
    const updated = {
      id: card?.id,
      title: titleValue ?? "",
      subtitle: subtitleValue ?? "",
      description: descriptionValue ?? "",
      background: backgroundChecked,
      noPadding: removePaddingChecked,
      separator: separadorValue === "" ? undefined : separadorValue,
      orientation:
        Object.keys(orientationObject).length === 0
          ? card?.orientation
          : orientationObject,
      split: dividirValue,
      divisionEqual: equalDivision,
      graphics: card?.graphics ?? [],
    } as DashCardsGraphics;
    updateCard(updated);
  };

  const orientationObject = useMemo(() => {
    if (breakpointsSelected.length === 0) return {};
    return breakpointsSelected.reduce(
      (obj, item) =>
        Object.assign(obj, { [item.breakpoint]: item.orientation }),
      {}
    );
  }, [breakpointsSelected]);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    updateParent();
  }, [
    titleValue,
    subtitleValue,
    descriptionValue,
    backgroundChecked,
    removePaddingChecked,
    separadorValue,
    orientationObject,
    dividirValue,
    equalDivision,
  ]);

  const handleAddBreakpoint = () => {
    const newBreakpoint = {
      id: Date.now().toString(),
      breakpoint: "sm",
      orientation: "horizontal",
    };
    setBreakpointsSelected((prev) => [...prev, newBreakpoint]);
  };

  function hasInitialOrientation(cardOrientation: string | Object) {
    if (typeof cardOrientation === "string") return false;
    return Object.keys(cardOrientation).length > 1;
  }
  function getInitialOrientation(cardOrientation: string | Object) {
    if (typeof cardOrientation === "string" && cardOrientation) {
      return [{ id: "1", breakpoint: "xs", orientation: cardOrientation }];
    }

    const result = Object.keys(cardOrientation).reduce<
      { id: string; breakpoint: string; orientation: string }[]
    >((acc, key, index) => {
      acc.push({
        id: (index + 1).toString(),
        breakpoint: key,
        orientation: (cardOrientation as Record<string, string>)[key],
      });
      return acc;
    }, []);

    const xsBreakpointIndex = result.findIndex(
      (item) => item.breakpoint === "xs"
    );
    if (xsBreakpointIndex > 0) {
      const [xsObject] = result.splice(xsBreakpointIndex, 1);
      result.unshift(xsObject);
    }

    return result;
  }

  const handleBreakpointChange = (e: SelectChangeEvent, id: string) => {
    const { value } = e.target;
    setBreakpointsSelected((prev) =>
      prev.map((breakpoint) =>
        breakpoint.id === id
          ? { ...breakpoint, breakpoint: value as string }
          : breakpoint
      )
    );
    updateParent();
  };

  const handleOrientationChange = (e: SelectChangeEvent, id: string) => {
    const { value } = e.target;
    setBreakpointsSelected((prev) =>
      prev.map((breakpoint) =>
        breakpoint.id === id
          ? { ...breakpoint, orientation: value as string }
          : breakpoint
      )
    );
    updateParent();
  };

  const handleDeleteBreakpoint = (id: string) => {
    setBreakpointsSelected((prev) =>
      prev.filter((breakpoint) => breakpoint.id !== id)
    );
    updateParent();
  };
  const handleDividirChange = (checked: boolean) => {
    setDividirValue(checked);
    if (card) {
      updateCard({ ...card, split: checked });
    }
  };

  const handleEqualDivisionChange = (checked: boolean) => {
    setEqualDivision(checked);
    if (card) {
      updateCard({ ...card, divisionEqual: checked });
    }
  };
  return (
    <Paper sx={styles.paper}>
      <Typography id="panel-geral-title">Geral</Typography>

      {!card?.split && !isTypeCard && (
        <>
          <TextField
            //sx={styles.textField}
            label="Título"
            variant="filled"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
          />
          <TextField
            //sx={styles.textField}
            label="Subtítulo"
            variant="filled"
            value={subtitleValue}
            onChange={(e) => setSubtitleValue(e.target.value)}
          />
          <TextField
            //sx={styles.textField}
            label="Descrição"
            variant="filled"
            multiline
            rows={3}
            value={descriptionValue}
            onChange={(e) => setDescriptionValue(e.target.value)}
          />
        </>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 2,
          //height: card?.split ? "100%" : "fit-content",
        }}
      >
        <FormControl sx={styles.switchFormControl}>
          <FormLabel
            sx={{
              color: theme.palette.text.primary,
              "&.Mui-focused": {
                color: theme.palette.text.primary,
              },
              fontSize: 14,
            }}
          >
            Plano de Fundo
          </FormLabel>
          <FormControlLabel
            value="bottom"
            control={
              <Switch
                sx={{
                  marginLeft: -1,
                }}
                checked={backgroundChecked}
                onChange={(e) => setBackgroundChecked(e.target.checked)}
              />
            }
            label="Adicionar Plano de Fundo"
            labelPlacement="top"
            sx={styles.switchLabel}
          />
        </FormControl>
        <FormControl sx={styles.switchFormControl}>
          <FormLabel
            sx={{
              color: theme.palette.text.primary,
              "&.Mui-focused": {
                color: theme.palette.text.primary,
              },
              fontSize: 14,
            }}
          >
            Remover padding
          </FormLabel>
          <FormControlLabel
            value="bottom"
            control={
              <Switch
                sx={{
                  marginLeft: -1,
                }}
                checked={removePaddingChecked}
                onChange={(e) => setRemovePaddingChecked(e.target.checked)}
              />
            }
            label="Remove o espaçamento interno"
            labelPlacement="top"
            sx={styles.switchLabel}
          />
        </FormControl>
      </Box>
      <Box>
        <Typography fontSize={14}>Separador</Typography>
        <FormControl
          sx={{
            display: "flex",
            justifyContent: "start",
            alignItems: "start",
          }}
        >
          <FormLabel
            sx={{
              marginBottom: 1,
              "&.Mui-focused": {
                color: theme.palette.text.secondary,
              },
              fontSize: 14,
            }}
          >
            Adiciona linha divisória
          </FormLabel>
          <Box sx={styles.radialContainer}>
            <FormControlLabel
              value="divider"
              control={
                <Radio
                  checked={separadorValue === "divider"}
                  onChange={(e) => setSeparadorValue(e.target.value as Divider)}
                />
              }
              label="Sólido"
              labelPlacement="start"
              sx={{
                marginRight: 4,
                marginLeft: 0,
                borderLeft: `2px solid ${theme.palette.text.primary}`,
                gap: 2,
                paddingLeft: 1,
                "& .MuiFormControlLabel-label": {
                  fontSize: "12px",
                },
              }}
            />
            <FormControlLabel
              value="dividerDashed"
              control={
                <Radio
                  checked={separadorValue === "dividerDashed"}
                  onChange={(e) => setSeparadorValue(e.target.value as Divider)}
                />
              }
              label="Tracejado"
              labelPlacement="start"
              sx={{
                marginRight: 4,
                marginLeft: 0,
                borderLeft: `2px dotted ${theme.palette.text.primary}`,
                gap: 2,
                paddingLeft: 1,
                "& .MuiFormControlLabel-label": {
                  fontSize: "12px",
                },
              }}
            />
            <FormControlLabel
              value=""
              control={
                <Radio
                  checked={separadorValue === ""}
                  onChange={(e) => setSeparadorValue(e.target.value as Divider)}
                />
              }
              label="Nenhum"
              labelPlacement="start"
              sx={{
                marginRight: 4,
                marginLeft: 0,
                gap: 2,
                "& .MuiFormControlLabel-label": {
                  fontSize: "12px",
                },
              }}
            />
          </Box>
        </FormControl>
        <Divider sx={{ paddingTop: 1.5 }} />
      </Box>
      <Box
        //height={card?.split ? "100%" : "fit-content"}
        sx={{
          display: "flex",
          alignItems: "end",
          justifyContent: "space-between",
        }}
      >
        <FormControl sx={styles.switchFormControl}>
          <FormLabel
            sx={{
              color: theme.palette.text.primary,
              "&.Mui-focused": {
                color: theme.palette.text.primary,
              },
              fontSize: 14,
            }}
          >
            Orientação do painel
          </FormLabel>
          <FormControlLabel
            value="bottom"
            control={
              <Switch
                sx={{
                  marginLeft: -1,
                }}
                checked={orientationValue}
                onChange={(e) => setOrientationValue(e.target.checked)}
              />
            }
            label="Adiciona orientação customizável ao painel"
            labelPlacement="top"
            sx={styles.switchLabel}
          />
        </FormControl>
        {orientationValue && (
          <Button
            sx={{ color: theme.palette.primary.main }}
            startIcon={<Add />}
            onClick={handleAddBreakpoint}
          >
            Adicionar
          </Button>
        )}
      </Box>
      {orientationValue &&
        breakpointsSelected.length > 0 &&
        breakpointsSelected.map((breakpointSize, index) => (
          <Box sx={{ display: "flex", gap: 2 }} key={breakpointSize.id}>
            <FormControl sx={{ flex: 1 }}>
              <TextField
                select
                label="Ponto de interrupção"
                variant="filled"
                value={breakpointSize.breakpoint}
                disabled={index === 0}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleBreakpointChange(e, breakpointSize.id)
                }
              >
                {["xs", "sm", "md", "mdl", "lg", "xl"]
                  .filter(
                    (bp) =>
                      !breakpointsSelected.some(
                        (obj) => obj.breakpoint === bp
                      ) || breakpointSize.breakpoint === bp
                  )
                  .map((size, index) => (
                    <MenuItem value={size} key={index}>
                      {size}
                    </MenuItem>
                  ))}
              </TextField>
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <TextField
                select
                label="Orientação"
                variant="filled"
                value={breakpointSize.orientation}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleOrientationChange(e, breakpointSize.id)
                }
              >
                <MenuItem value={"horizontal"}>Horizontal</MenuItem>
                <MenuItem value={"vertical"}>Vertical</MenuItem>
              </TextField>
            </FormControl>
            <IconButton
              sx={{ color: theme.palette.cromatic.red.main }}
              disabled={index === 0}
              onClick={() => handleDeleteBreakpoint(breakpointSize.id)}
            >
              <GridDeleteIcon />
            </IconButton>
          </Box>
        ))}
      <Box sx={styles.dividerContainer}>
        <FormControl sx={styles.switchFormControl}>
          <FormLabel
            sx={{
              color: theme.palette.text.primary,
              "&.Mui-focused": {
                color: theme.palette.text.primary,
              },
              fontSize: 14,
            }}
          >
            Dividir
          </FormLabel>
          <FormControlLabel
            value="bottom"
            control={
              <Tooltip title={"Adicione mais de um grafico para ativar"}>
                <Box>
                  <Switch
                    disabled={card?.graphics && card?.graphics.length <= 1}
                    sx={{
                      marginLeft: -1,
                    }}
                    checked={dividirValue}
                    onChange={(e) => handleDividirChange(e.target.checked)}
                  />
                </Box>
              </Tooltip>
            }
            label="Configura os gráficos individualmente"
            labelPlacement="top"
            sx={styles.switchLabel}
          />
        </FormControl>
        <FormControl sx={styles.switchFormControl}>
          <FormLabel
            sx={{
              color: theme.palette.text.primary,
              "&.Mui-focused": {
                color: theme.palette.text.primary,
              },
              fontSize: 14,
            }}
          >
            Divisão igual
          </FormLabel>
          <FormControlLabel
            value="bottom"
            control={
              <Tooltip title={"Adicione mais de um grafico para ativar"}>
                <Box>
                  <Switch
                    disabled={card?.graphics && card?.graphics.length <= 1}
                    sx={{
                      marginLeft: -1,
                    }}
                    checked={equalDivision}
                    onChange={(e) =>
                      handleEqualDivisionChange(e.target.checked)
                    }
                  />
                </Box>
              </Tooltip>
            }
            label="Remove o espaçamento interno"
            labelPlacement="top"
            sx={styles.switchLabel}
          />
        </FormControl>
      </Box>
    </Paper>
  );
};

export default GeralMenu;
